import type { FC } from "react";

import { useTranslation } from "react-i18next";
import { Pencil, Star, Trash2 } from "lucide-react";

import type { ContractContactAlertType, ContractContactGroup } from "../types";
import type { ContactAlertToggle, ContactDestinationRow } from "../utils/contract-contact-row";

import { useToggleAlertService } from "../services/use-toggle-alert-service";
import { serverErrorMessage } from "../utils/apply-server-errors";
import { contactDestinationRows } from "../utils/contract-contact-row";
import { formatPhone } from "../utils/destination";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface OccupantsTableProps {
  contractId: string | number;
  groups: ContractContactGroup[];
  onEdit: (contactId: string) => void;
  onDelete: (group: ContractContactGroup) => void;
}

/**
 * The contract's contacts, one block per person: their destinations listed down
 * the Details column, each with the alert types it can carry. The Contact and
 * Actions cells span the block, so a contact is named once however many numbers
 * and addresses it has. With no contact, the header stays and a single "No
 * entries" row stands in for the blocks.
 */
const OccupantsTable: FC<OccupantsTableProps> = ({ contractId, groups, onEdit, onDelete }) => {
  const { t } = useTranslation(["occupants"]);
  const toggleAlert = useToggleAlertService(contractId);
  const isEmpty = groups.length === 0;

  // Shorter than the drawer's: the column heading already says these are alerts.
  const alertLabels: Record<ContractContactAlertType, string> = {
    call: t("occupants:occupants-alert-call", "Call"),
    sms: t("occupants:occupants-alert-sms", "SMS"),
    email: t("occupants:occupants-alert-email", "Email"),
  };

  // A refused box springs back on the refetch; this says why rather than
  // leaving the click looking ignored.
  const toggleError = toggleAlert.isError
    ? (serverErrorMessage(toggleAlert.error) ??
      t("occupants:occupants-toggle-error", "Failed to update the alert. Please try again."))
    : null;

  const toggle = (contactId: string, destination: ContactDestinationRow, alert: ContactAlertToggle) =>
    toggleAlert.mutate({
      contactId,
      alertType: alert.alertType,
      destination: destination.value,
      contractContactId: alert.contractContactId,
    });

  return (
    <>
      {toggleError && (
        <p className="mx-8 my-3 rounded-md bg-destructive/10 p-3 text-sm text-destructive" role="alert">
          {toggleError}
        </p>
      )}

      <Table className="min-w-[860px] table-fixed">
        <TableHeader>
          <TableRow>
            {/* The column rules only frame contact blocks; an empty table has none. */}
            <TableHead
              className={`h-10 w-[260px] px-4 text-sm font-medium text-muted-foreground ${isEmpty ? "" : "border-r"}`}
            >
              {t("occupants:occupants-column-contact", "Contact")}
            </TableHead>
            <TableHead className="h-10 min-w-[200px] px-4 text-sm font-medium text-muted-foreground">
              {t("occupants:occupants-column-details", "Details")}
            </TableHead>
            <TableHead className="h-10 w-[240px] px-4 text-sm font-medium text-muted-foreground">
              {t("occupants:occupants-column-alerts", "Alert management")}
            </TableHead>
            <TableHead
              className={`h-10 w-[128px] px-4 text-sm font-medium text-muted-foreground ${isEmpty ? "" : "border-l"}`}
            >
              {t("occupants:occupants-column-actions", "Actions")}
            </TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {isEmpty && (
            <TableRow className="hover:bg-transparent">
              <TableCell className="h-[120px] px-4 text-center text-base text-muted-foreground" colSpan={4}>
                {t("occupants:occupants-empty", "No entries")}
              </TableCell>
            </TableRow>
          )}

          {groups.map((group) => {
            const destinations = contactDestinationRows(group);
            // A contact with nothing on file still gets a row, so it can be
            // edited or removed rather than vanishing from the table.
            const rowCount = Math.max(destinations.length, 1);

            return destinations.length === 0 ? (
              <TableRow key={group.contactId}>
                <ContactCell group={group} rowSpan={1} />
                <TableCell className="h-12 px-4 text-sm text-muted-foreground" colSpan={2}>
                  {t("occupants:occupants-no-destination", "No phone number or email on file")}
                </TableCell>
                <ActionsCell group={group} rowSpan={1} onDelete={onDelete} onEdit={onEdit} />
              </TableRow>
            ) : (
              destinations.map((destination, index) => (
                <TableRow key={`${group.contactId}-${destination.key}`}>
                  {/* Spans the block: rendered on the first destination only. */}
                  {index === 0 && <ContactCell group={group} rowSpan={rowCount} />}

                  <TableCell className="h-12 whitespace-normal px-4 py-2 align-middle text-sm font-medium text-foreground">
                    <span className="flex flex-wrap items-center gap-x-2">
                      {/* Shown dashed; the stored value is what the boxes write. */}
                      <span className="break-all">
                        {destination.kind === "phone" ? formatPhone(destination.value) : destination.value}
                      </span>
                      {destination.label && <span className="text-muted-foreground">({destination.label})</span>}
                    </span>
                  </TableCell>

                  <TableCell className="h-12 w-[240px] px-4 py-2 align-middle">
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5">
                      {destination.alerts.map((alert) => (
                        <label key={alert.alertType} className="flex cursor-pointer items-center gap-2">
                          <Checkbox
                            checked={alert.checked}
                            disabled={toggleAlert.isPending}
                            onCheckedChange={() => toggle(group.contactId, destination, alert)}
                          />
                          <span className="whitespace-nowrap text-sm leading-none text-foreground">
                            {alertLabels[alert.alertType]}
                          </span>
                        </label>
                      ))}
                    </div>
                  </TableCell>

                  {index === 0 && (
                    <ActionsCell group={group} rowSpan={rowCount} onDelete={onDelete} onEdit={onEdit} />
                  )}
                </TableRow>
              ))
            );
          })}
        </TableBody>
      </Table>
    </>
  );
};

/** The person's name, once per block. */
const ContactCell: FC<{ group: ContractContactGroup; rowSpan: number }> = ({ group, rowSpan }) => {
  const { t } = useTranslation(["occupants"]);

  return (
    <TableCell className="w-[260px] border-r px-4 py-2 align-top text-sm font-medium text-foreground" rowSpan={rowSpan}>
      <span className="flex items-start gap-1.5 py-1">
        <span className="whitespace-normal break-words">
          {group.name || t("occupants:occupants-unnamed-contact", "Unnamed contact")}
        </span>
        {/* The contract's primary contact, as the row reports it. */}
        {group.isPrimaryContact && (
          <Star
            aria-label={t("occupants:occupants-primary-contact", "Primary contact")}
            className="mt-0.5 size-3.5 shrink-0 fill-primary text-primary"
          />
        )}
      </span>
    </TableCell>
  );
};

interface ActionsCellProps {
  group: ContractContactGroup;
  rowSpan: number;
  onEdit: (contactId: string) => void;
  onDelete: (group: ContractContactGroup) => void;
}

const ActionsCell: FC<ActionsCellProps> = ({ group, rowSpan, onEdit, onDelete }) => {
  const { t } = useTranslation(["occupants"]);

  return (
    <TableCell className="w-[128px] border-l px-4 py-2 align-top" rowSpan={rowSpan}>
      <div className="flex items-start gap-2">
        <Button
          aria-label={t("occupants:occupants-edit-contact", "Edit this contact")}
          className="cursor-pointer"
          size="icon-sm"
          type="button"
          variant="ghost"
          onClick={() => onEdit(group.contactId)}
        >
          <Pencil className="size-4 text-primary" />
        </Button>
        <Button
          aria-label={t("occupants:occupants-delete-contact", "Remove this contact")}
          className="cursor-pointer"
          size="icon-sm"
          type="button"
          variant="ghost"
          onClick={() => onDelete(group)}
        >
          <Trash2 className="size-4 text-destructive" />
        </Button>
      </div>
    </TableCell>
  );
};

export default OccupantsTable;
